import {
  POLICY_ROUTING_PRIORITY_BASE,
  POLICY_ROUTING_TABLE_BASE,
  POLICY_ROUTING_TEMPLATES,
} from '@connaro/shared';
import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, EmptyState, ErrorState, Loading } from './states';

interface Rule {
  id: string;
  mark: number;
  tableId: number;
  priority: number;
  template: string | null;
  description: string;
}

interface Route {
  id: string;
  tableId: number;
  destination: string;
  gateway: string | null;
  device: string | null;
  blackhole: boolean;
  template: string | null;
}

/**
 * Policy routing for one device: the `ip rule` mark bindings, the routes in their
 * tables, and the named templates.
 *
 * Policy routing steers marked traffic — a firewall rule marks a packet, a rule here
 * sends that mark to an alternate routing table, and the routes in that table decide
 * the next hop. Everything lives in Connaro's reserved priority band and table
 * range, which the backend enforces, so nothing here can disturb the host's own
 * routing. The rendered preview is the exact ip-batch the edge applies.
 */
export function PolicyRoutingPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const t = useT();
  const rules = useAsync(
    () => api.get<Rule[]>(`/policy-routing/devices/${deviceId}/rules`),
    [deviceId],
  );
  const routes = useAsync(
    () => api.get<Route[]>(`/policy-routing/devices/${deviceId}/routes`),
    [deviceId],
  );
  const rendered = useAsync(
    () => api.get<{ batch: string }>(`/policy-routing/devices/${deviceId}/rendered`),
    [deviceId],
  );

  const [error, setError] = useState<string | null>(null);
  const [template, setTemplate] = useState<string>(POLICY_ROUTING_TEMPLATES[0]);
  const [mark, setMark] = useState('1');
  const [table, setTable] = useState(String(POLICY_ROUTING_TABLE_BASE + 1));
  const [priority, setPriority] = useState(String(POLICY_ROUTING_PRIORITY_BASE + 1));
  const [gateway, setGateway] = useState('');
  const [device, setDevice] = useState('');
  const [prefixes, setPrefixes] = useState('');

  const reloadAll = (): void => {
    rules.reload();
    routes.reload();
    rendered.reload();
  };

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await fn();
      reloadAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const applyTemplate = (e: React.FormEvent): void => {
    e.preventDefault();
    const list = prefixes
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    void run(() =>
      api.post(`/policy-routing/devices/${deviceId}/templates`, {
        template,
        mark: Number(mark),
        table: Number(table),
        priority: Number(priority),
        ...(gateway ? { gateway } : {}),
        ...(device ? { device } : {}),
        ...(list.length > 0 ? { prefixes: list } : {}),
      }),
    );
  };

  return (
    <div className="card">
      <h2>{t('policyRouting.title')}</h2>
      <p>{t('policyRouting.intro')}</p>
      {error ? <Banner variant="error">{error}</Banner> : null}

      <form className="card form" onSubmit={applyTemplate}>
        <h3>{t('policyRouting.applyTemplate')}</h3>
        <label className="field">
          <span>{t('policyRouting.template')}</span>
          <select value={template} onChange={(e) => setTemplate(e.target.value)}>
            {POLICY_ROUTING_TEMPLATES.map((tpl) => (
              <option key={tpl} value={tpl}>
                {t(`policyRouting.templates.${tpl}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('policyRouting.mark')}</span>
          <input value={mark} onChange={(e) => setMark(e.target.value)} inputMode="numeric" />
        </label>
        <label className="field">
          <span>{t('policyRouting.table')}</span>
          <input value={table} onChange={(e) => setTable(e.target.value)} inputMode="numeric" />
        </label>
        <label className="field">
          <span>{t('policyRouting.priority')}</span>
          <input
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            inputMode="numeric"
          />
        </label>
        <label className="field">
          <span>{t('policyRouting.gateway')}</span>
          <input
            placeholder="192.0.2.1"
            value={gateway}
            onChange={(e) => setGateway(e.target.value)}
          />
        </label>
        <label className="field">
          <span>{t('policyRouting.device')}</span>
          <input placeholder="wg0" value={device} onChange={(e) => setDevice(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('policyRouting.prefixes')}</span>
          <input
            placeholder="10.0.0.0/8, 192.168.0.0/16"
            value={prefixes}
            onChange={(e) => setPrefixes(e.target.value)}
          />
        </label>
        <button type="submit" className="btn">
          {t('policyRouting.apply')}
        </button>
      </form>

      <h3>{t('policyRouting.rules')}</h3>
      {rules.loading ? (
        <Loading />
      ) : rules.error ? (
        <ErrorState message={rules.error.message} onRetry={rules.reload} />
      ) : !rules.data || rules.data.length === 0 ? (
        <EmptyState hint={t('policyRouting.noRules')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('policyRouting.mark')}</th>
                <th>{t('policyRouting.table')}</th>
                <th>{t('policyRouting.priority')}</th>
                <th>{t('policyRouting.template')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rules.data.map((r) => (
                <tr key={r.id}>
                  <td>
                    <code>0x{r.mark.toString(16)}</code>
                  </td>
                  <td>{r.tableId}</td>
                  <td>{r.priority}</td>
                  <td>{r.template ?? '—'}</td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--danger"
                      onClick={() => void run(() => api.del(`/policy-routing/rules/${r.id}`))}
                    >
                      {t('policyRouting.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>{t('policyRouting.routes')}</h3>
      {routes.loading ? (
        <Loading />
      ) : routes.error ? (
        <ErrorState message={routes.error.message} onRetry={routes.reload} />
      ) : !routes.data || routes.data.length === 0 ? (
        <EmptyState hint={t('policyRouting.noRoutes')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('policyRouting.table')}</th>
                <th>{t('policyRouting.destination')}</th>
                <th>{t('policyRouting.nextHop')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {routes.data.map((r) => (
                <tr key={r.id}>
                  <td>{r.tableId}</td>
                  <td>
                    <code>{r.destination}</code>
                  </td>
                  <td>
                    {r.blackhole
                      ? t('policyRouting.blackhole')
                      : [r.gateway, r.device].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--danger"
                      onClick={() => void run(() => api.del(`/policy-routing/routes/${r.id}`))}
                    >
                      {t('policyRouting.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>{t('policyRouting.rendered')}</h3>
      {rendered.data && rendered.data.batch.trim().length > 0 ? (
        <pre className="code-block">{rendered.data.batch}</pre>
      ) : (
        <EmptyState hint={t('policyRouting.noRendered')} />
      )}
    </div>
  );
}
